export interface OrderLike {
	id?: string;
	code?: string;
	customer_name?: string;
	status_name?: string;
	status_id?: string;
	status?: {
		id?: string;
		name?: string;
		color?: string;
	};
	shipping_address?: string;
	billing_address?: string;
	total_amount?: number | string | null;
	[key: string]: unknown;
}

export interface FormattedOrderOption {
	id: string;
	label: string;
	displayLabel: string;
	addressLabel: string;
	customerName: string;
	address: string;
	totalAmount: number | null;
	statusColor: string;
	line1Color: string;
	line2Color: string;
}

const pickString = (...values: unknown[]): string => {
	for (const value of values) {
		if (typeof value === 'string' && value.trim()) {
			return value.trim();
		}
		if (typeof value === 'number') {
			return String(value);
		}
	}
	return '';
};

const pickAddressString = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value.trim();
	if (typeof value !== 'object') return '';

	const address = value as {
		full_address?: unknown;
		address_line?: unknown;
		free_address?: unknown;
		name?: unknown;
		label?: unknown;
	};

	return pickString(
		address.full_address,
		address.address_line,
		address.free_address,
		address.name,
		address.label,
	);
};

/** Resolve customer display name from flat or nested order payload. */
export const resolveOrderCustomerName = (order: OrderLike | null | undefined): string => {
	if (!order) return '';
	const orderAny = order as Record<string, unknown>;
	const customer = orderAny.customer;
	return pickString(
		order.customer_name,
		typeof customer === 'object' && customer !== null && 'name' in customer
			? (customer as { name?: string }).name
			: undefined,
		typeof customer === 'string' ? customer : undefined,
	);
};

const pickStatusMeta = (order: OrderLike): { statusName: string; statusColor?: string } => {
	const orderAny = order as any;

	const statusName = pickString(
		order.status_name,
		order.status?.name,
		orderAny.sales_status_name,
		orderAny.sale_status_name,
		orderAny.sales_order_status_name,
		orderAny.qldh_status_name,
		orderAny.order_management_status_name,
		orderAny.operation_status_name,
		orderAny.accounting_status_name,
		orderAny.accountant_status_name,
		orderAny.status_display,
		'pending',
	);

	const statusColor = pickString(
		order.status?.color,
		orderAny.sales_status?.color,
		orderAny.sale_status?.color,
		orderAny.sales_order_status?.color,
		orderAny.qldh_status?.color,
		orderAny.order_management_status?.color,
		orderAny.operation_status?.color,
		orderAny.accounting_status?.color,
		orderAny.accountant_status?.color,
		orderAny.status_color,
	);

	return {
		statusName,
		statusColor: statusColor || undefined,
	};
};

export const getStatusColorFromConfig = (
	_status: string,
	_statusId?: string,
	statusColor?: string,
): string => {
	// Keep the same behavior expectation: color should come from API config.
	return statusColor || '#64748b';
};

export const resolveOrderStatusMeta = (
	order: OrderLike | null | undefined,
): { statusName: string; statusColor: string } => {
	if (!order) {
		return { statusName: '', statusColor: '#64748b' };
	}
	const { statusName, statusColor: statusColorFromOrder } = pickStatusMeta(order);
	return {
		statusName,
		statusColor: getStatusColorFromConfig(statusName, order.status_id, statusColorFromOrder),
	};
};

export interface OrderProgressOption {
	id: string;
	code: string;
	customer_name: string;
	label: string;
	status_name: string;
	status_color: string;
	line1Color: string;
	line2Color: string;
	addressLabel: string;
}

export const getOrderDeliveryAddress = (order: OrderLike): string => {
	const deliveryAddressLine = order.delivery_address_line;
	const deliveryAddress = order.delivery_address;
	const fullAddress = order.full_address;

	return pickString(
		pickAddressString(deliveryAddressLine),
		pickAddressString(deliveryAddress),
		pickAddressString(order.shipping_address),
		pickAddressString(order.billing_address),
		pickAddressString(fullAddress),
	);
};

export const normalizeOrderTotalAmount = (rawAmount: unknown): number | null => {
	if (rawAmount === null || rawAmount === undefined) {
		return null;
	}

	const amount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : Number(rawAmount);
	return isNaN(amount) ? null : amount;
};

export const formatVndAmount = (amount: number | null): string => {
	if (amount === null) {
		return '';
	}

	return amount.toLocaleString('vi-VN');
};

export const formatOrderOption = (order: OrderLike): FormattedOrderOption => {
	const code = String(order.code || order.id || '');
	const customerName = resolveOrderCustomerName(order);
	const { statusName, statusColor: statusColorFromOrder } = pickStatusMeta(order);
	const address = getOrderDeliveryAddress(order);
	const totalAmount = normalizeOrderTotalAmount(order.total_amount);
	const formattedTotalAmount = totalAmount === 0 ? '' : formatVndAmount(totalAmount);
	const statusColor = getStatusColorFromConfig(statusName, order.status_id, statusColorFromOrder);

	return {
		id: String(order.id || ''),
		label: code,
		displayLabel: [code, customerName, formattedTotalAmount].filter(Boolean).join(' - '),
		addressLabel: `${address || ''}`,
		customerName,
		address,
		totalAmount,
		statusColor,
		line1Color: statusColor,
		line2Color: '#6b7280',
	};
};

/** Order option for progress modals: `code: customer` label + status color from API. */
export const formatOrderProgressOption = (order: OrderLike): OrderProgressOption => {
	const formatted = formatOrderOption(order);
	const code = String(order.code || order.id || 'N/A');
	const customerName = resolveOrderCustomerName(order);
	const { statusName, statusColor } = resolveOrderStatusMeta(order);
	const label = customerName ? `${code}: ${customerName}` : code;

	return {
		id: formatted.id,
		code,
		customer_name: customerName,
		label,
		status_name: statusName,
		status_color: statusColor,
		line1Color: statusColor,
		line2Color: '#6b7280',
		addressLabel: formatted.addressLabel,
	};
};
